import { useEffect } from "react";
import {
  fetchAbstractMetadata,
  fetchTracks,
} from "../api/conferenceRegistrationApi";
import {
  ABSTRACT_STEP_MODES,
  normalizeAbstractStepNumber,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
} from "../registration/registrationModules";

export const usePaperMetadata = ({
  conferenceApi,
  conferenceId,
  paid,
  step,
  hasPaperSubmitted,
  setAbstractConfig,
  setAbstractStepNumber,
  setMaxAbstractChars,
  setMaxFileSizeMb,
  setPresentationMode,
  setTracks,
  setAbstractMetadataLoaded,
}) => {
  useEffect(() => {
    if (!conferenceId) {
      setAbstractMetadataLoaded?.(false);
      return;
    }

    let ignoreStaleResponse = false;
    setAbstractMetadataLoaded?.(false);

    const loadAbstractMetadata = async () => {
      try {
        const metadata = await fetchAbstractMetadata({
          conferenceApi,
          conferenceId,
        });

        if (ignoreStaleResponse) {
          return;
        }

        console.log("Abstract metadata:", metadata);
        console.log("Co-authors enabled:", metadata?.coAuthors);

        if (metadata) {
          setAbstractConfig(metadata);
          const stepNumber = normalizeAbstractStepNumber(
            metadata.abstractStepNumber
          );

          if (
            (paid === true || paid === "true") &&
            stepNumber === ABSTRACT_STEP_MODES.AFTER_PAYMENT &&
            !hasPaperSubmitted &&
            step !== POST_PAYMENT_ABSTRACT_FORM_STEP
          ) {
            console.log(
              "💳 User has paid but paper NOT submitted - keeping abstractStepNumber at 7"
            );
            console.log(
              "   Button in summary page will allow navigation to step 7 for paper submission"
            );
          } else if (
            (paid === true || paid === "true") &&
            stepNumber === ABSTRACT_STEP_MODES.AFTER_PAYMENT &&
            step === POST_PAYMENT_ABSTRACT_FORM_STEP
          ) {
            console.log(
              "💳 User just completed payment at step 7 - keeping abstractStepNumber at 7"
            );
            console.log(
              "   This allows abstract form to show immediately after payment"
            );
          } else if (!paid && stepNumber === ABSTRACT_STEP_MODES.AFTER_PAYMENT) {
            console.log(
              "⏳ User has NOT paid - keeping abstractStepNumber at 7 (pay & register mode)"
            );
            console.log("   Abstract will be shown at step 7 after payment");
          }

          setAbstractStepNumber(stepNumber);
          console.log("Abstract step number (final):", stepNumber);
          setMaxAbstractChars(metadata.maxAbstractChars || 1000);
          setMaxFileSizeMb(metadata.maxFileSizeMb || 10);

          if (metadata.mode) {
            const modes = metadata.mode.split(",").map((mode) => mode.trim());
            if (modes.length === 1) {
              setPresentationMode(modes[0]);
            }
          }
        } else {
          setAbstractStepNumber(0);
          setMaxAbstractChars(1000);
          setMaxFileSizeMb(10);
        }
      } catch (error) {
        if (ignoreStaleResponse) {
          return;
        }

        console.error("Failed to fetch abstract metadata:", error);
        setAbstractStepNumber(0);
        setMaxAbstractChars(1000);
      } finally {
        if (!ignoreStaleResponse) {
          setAbstractMetadataLoaded?.(true);
        }
      }
    };

    loadAbstractMetadata();

    return () => {
      ignoreStaleResponse = true;
    };
  }, [
    conferenceApi,
    conferenceId,
    paid,
    step,
    hasPaperSubmitted,
    setAbstractConfig,
    setAbstractStepNumber,
    setMaxAbstractChars,
    setMaxFileSizeMb,
    setPresentationMode,
    setAbstractMetadataLoaded,
  ]);

  useEffect(() => {
    console.log("🎯 Tracks useEffect triggered - conferenceId:", conferenceId);

    if (!conferenceId) {
      console.log("⚠️ Cannot fetch tracks - conferenceId is null/undefined");
      return;
    }

    const loadTracks = async () => {
      try {
        const tracksArray = await fetchTracks({
          conferenceApi,
          conferenceId,
        });
        console.log("✅ Tracks fetched successfully:", tracksArray);
        console.log(
          "📋 Setting tracks array (length " + tracksArray.length + "):",
          tracksArray
        );
        setTracks(tracksArray);
      } catch (error) {
        console.error("❌ Failed to fetch tracks:", error);
        setTracks([]);
      }
    };

    loadTracks();
  }, [conferenceApi, conferenceId, setTracks]);
};
